import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";

/**
 * Handles the OAuth redirect from Emergent — runs when the URL contains
 * `#session_id=...`. Calls our backend `/auth/google/session` to exchange
 * the one-time id for a persistent `session_token` cookie, then routes
 * the user into the app (or to the pending-approval screen).
 */
export default function AuthCallback() {
  const nav = useNavigate();
  const loc = useLocation();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [status, setStatus] = useState("Signing you in with Google…");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = loc.hash || window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    if (!sessionId) {
      nav("/login", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setUser(data.user);
        // Strip the fragment from the URL
        try {
          window.history.replaceState({}, "", window.location.pathname);
        } catch {}
        if (data.pending) {
          toast.info("Account created — awaiting admin approval");
          nav("/pending-approval", { replace: true, state: { user: data.user } });
        } else {
          toast.success("Signed in with Google");
          nav("/", { replace: true });
        }
      } catch (e) {
        setStatus("");
        toast.error(formatApiError(e.response?.data?.detail) || "Google sign-in failed");
        nav("/login", { replace: true });
      }
    })();
  }, [loc.hash, nav, setUser]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6">
      <BrandLogo size={56} fallbackChar="M" />
      <div className="mt-6 text-slate-700 font-heading text-xl font-semibold">{status || "Redirecting…"}</div>
      <div className="text-slate-400 text-xs mt-2">Please wait — this only takes a moment.</div>
    </div>
  );
}
